/**
 * The restaurant locations — one canonical definition.
 *
 * Before this module the pair was re-declared in roughly ten files, each with
 * its own shape ({id,name,displayName}, {value,label}, a bare array, a
 * VALID_* list...). That drift is why `isKnownLocation` lives here too: the
 * loose validator in utils/guestDataValidator.ts accepts bare "Franklin" and
 * only *warns* on an unknown value, which is fine for a create-time nudge but
 * useless as a write guard. A location string that isn't exactly one of these
 * orphans the guest from every location-scoped query, cache key and solver
 * run in the system.
 *
 * Pure module: no prisma, no twilio, no next. Importable from client bundles,
 * and reachable by the `node --test` glob over src/lib.
 */

export const MIMOSA_BROOKFIELD = 'Mimosa Brookfield';
export const MIMOSA_FRANKLIN = 'Mimosa Franklin';

/** Every location, in the order they should be offered in pickers. */
export const LOCATIONS: readonly string[] = [MIMOSA_BROOKFIELD, MIMOSA_FRANKLIN];

/** URL slug -> stored display name. The stored form is the display name. */
export const SLUG_TO_LOCATION: Record<string, string> = {
  'mimosa-brookfield': MIMOSA_BROOKFIELD,
  'mimosa-franklin': MIMOSA_FRANKLIN,
};

/** Stored display name -> URL slug. Exact inverse of SLUG_TO_LOCATION. */
export const LOCATION_TO_SLUG: Record<string, string> = {
  [MIMOSA_BROOKFIELD]: 'mimosa-brookfield',
  [MIMOSA_FRANKLIN]: 'mimosa-franklin',
};

/**
 * Is this exactly a location we store? Deliberately strict — no trimming, no
 * case folding, no accepting the slug or the bare town name. Anything looser
 * would let a near-miss through, and a near-miss is indistinguishable from a
 * correct value at every read site downstream.
 */
export function isKnownLocation(value: unknown): value is string {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LOCATION_TO_SLUG, value);
}

/**
 * The other location, for a two-location world. Null when the input isn't a
 * known location, so callers can't accidentally "toggle" a typo into a real
 * value.
 */
export function otherLocation(location: string): string | null {
  if (!isKnownLocation(location)) return null;
  return LOCATIONS.find(l => l !== location) ?? null;
}

/** "Mimosa Brookfield" -> "Brookfield". Used in space-tight UI and SMS. */
export function shortLocationName(location: string): string {
  return location.replace(/^Mimosa\s+/i, '');
}

/**
 * Clean a set of locations for storage on Employee.locations: keep only known
 * values, drop duplicates, and return them in LOCATIONS order so the stored
 * array is stable (two employees at both restaurants compare equal, and the
 * UI never reorders under the manager).
 *
 * Accepts a single string or an array, so callers can pass a legacy scalar
 * straight through. Unknown values are DROPPED rather than rejected — these
 * feed `hasSome` filters that drive SMS fan-out, and a junk string silently
 * widens who gets texted.
 */
export function normalizeLocations(input: unknown): string[] {
  const raw = Array.isArray(input) ? input : input == null ? [] : [input];
  const seen = new Set<string>();
  for (const v of raw) {
    if (isKnownLocation(v)) seen.add(v);
  }
  return LOCATIONS.filter(l => seen.has(l));
}
